import { PlusOutlined, TranslationOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Flex, Menu } from "antd";
import Layout, { Content, Header } from "antd/lib/layout/layout";
import Sider from "antd/lib/layout/Sider";
import to from "await-to-js";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import { LANGUAGE_OPTIONS, Locales } from "../../i18n";
import style from "./SideLayout.module.scss";

import { authService } from "../../modules/auth/auth.service";
import { MinistryContextProvider } from "../../modules/ministry/ministry.context";
import { AuthorisedPersonsContextProvider } from "../../modules/obligee/authorisedPersons/authorisedPersons.context";
import { ExciseLicencesContextProvider } from "../../modules/obligee/exciseLicences/exciseLicences.context";
import { ExciseStampsContextProvider } from "../../modules/obligee/exciseStamps/exciseStamps.context";
import { ExciseWarehouseContextProvider } from "../../modules/obligee/exciseWarehouse/exciseWarehouse.context";
import storage from "../../utils/storage";
import { LogoutBtn } from "../LogoutBtn/LogoutBtn";
import { Select } from "../Select/Select";

import { ItemType, MenuItemType } from "antd/lib/menu/hooks/useItems";
import { ModalsContext } from "../../context/modals.context";
import { AuthContext } from "../../modules/auth/auth.context";
import { mapSelectOptions } from "../../utils/map4select";

import dayjs from "dayjs";
import { localeObjectSerbianDayjs } from "../../locale/antLocales";
import { USER_REDIRECT_MAP } from "../../modules/auth/auth.constants";
import { CentralRegistryContextProvider } from "../../modules/ministry/centralRegistry/centralRegistry.context";
import { PeriodicReportsContextProvider } from "../../modules/obligee/periodicReports/periodicReports.context";
import { RecordsContextProvider } from "../../modules/obligee/records/records.context";
import { TrackAndTraceContextProvider } from "../../modules/trackAndTrace/trackAndTrace.context";

export interface IMenuItem {
  isVisible: boolean;
  children?: IMenuItem[];
}

const routeAccessReducer = (acc: IMenuItem[], route: IMenuItem) => {
  if (route.children) {
    route.children = route.children.reduce(routeAccessReducer, []);
  }
  return route.isVisible ? [...acc, route] : acc;
};

const getDefaultOpenMenuItem = () => {
  const url = window.location.href;
  if (url.includes("obveznik/akcizneMarkice/noviZahtev"))
    return {
      current: "/obveznik/akcizneMarkice/noviZahtev",
      open: ["/obveznik/akcizneMarkice", "akcizneMarkice"],
    };
  if (url.includes("obveznik/akcizneMarkice/evidencijaIskoriscenihMarkica"))
    return {
      current: "/obveznik/akcizneMarkice/evidencijaIskoriscenihMarkica",
      open: ["/obveznik/akcizneMarkice", "akcizneMarkice"],
    };
  if (url.includes("obveznik/akcizneMarkice/periodicniIzvestaji"))
    return {
      current: "/obveznik/akcizneMarkice/periodicniIzvestaji",
      open: ["/obveznik/akcizneMarkice", "akcizneMarkice"],
    };
  if (url.includes("obveznik/akcizneMarkice/listaIskoriscenihMarkica"))
    return {
      current: "/obveznik/akcizneMarkice/listaIskoriscenihMarkica",
      open: ["/obveznik/akcizneMarkice", "akcizneMarkice"],
    };
  if (url.includes("obveznik/akcizneMarkice/ovlascenaLica"))
    return {
      current: "/obveznik/akcizneMarkice/ovlascenaLica",
      open: ["/obveznik/akcizneMarkice", "akcizneMarkice"],
    };
  if (url.includes("obveznik/podesavanja"))
    return {
      current: "/obveznik/podesavanja",
      open: [],
    };
  if (url.includes("obveznik/puObrasci"))
    return {
      current: "/obveznik/puObrasci",
      open: [],
    };
  if (url.includes("obveznik/trackAndTrace/pracenje"))
    return {
      current: "/obveznik/trackAndTrace/pracenje",
      open: ["obveznik/trackAndTrace", "/obveznik/trackAndTrace"],
    };
  if (url.includes("obveznik/trackAndTrace/istorija"))
    return {
      current: "/obveznik/trackAndTrace/istorija",
      open: ["obveznik/trackAndTrace", "/obveznik/trackAndTrace"],
    };
  if (url.includes("obveznik/produkcioniKodovi"))
    return {
      current: "/obveznik/produkcioniKodovi",
      open: [],
    };
  // MFIN
  if (url.includes("mfin/podesavanja"))
    return {
      current: "/mfin/podesavanja",
      open: [],
    };
  if (url.includes("mfin/listaEvidentiranihMarkica"))
    return {
      current: "/mfin/listaEvidentiranihMarkica",
      open: ["mfin/akcizneMarkice", "/mfin/akcizneMarkice"],
    };
  if (url.includes("mfin/listaMarkica"))
    return {
      current: "/mfin/listaMarkica",
      open: ["mfin/akcizneMarkice", "/mfin/akcizneMarkice"],
    };
  if (url.includes("mfin/listaDozvola"))
    return {
      current: "/mfin/listaDozvola",
      open: [],
    };
  if (url.includes("mfin/listaDozvola"))
    return {
      current: "/mfin/listaDozvola",
      open: [],
    };
  if (url.includes("mfin/produkcioniKodovi/listaIzvoda"))
    return {
      current: "/mfin/produkcioniKodovi/listaIzvoda",
      open: ["mfin/produkcioniKodovi", "/mfin/produkcioniKodovi"],
    };
  if (url.includes("mfin/produkcioniKodovi/lista"))
    return {
      current: "/mfin/produkcioniKodovi/lista",
      open: ["mfin/produkcioniKodovi", "/mfin/produkcioniKodovi"],
    };
  if (url.includes("mfin/puObrasci"))
    return {
      current: "/mfin/puObrasci",
      open: [],
    };
  if (url.includes("mfin/periodicniIzvestaji/detalji"))
    return {
      current: "/mfin/periodicniIzvestaji/detalji",
      open: ["mfin/periodicniIzvestaji", "/mfin/periodicniIzvestaji"],
    };
  if (url.includes("mfin/periodicniIzvestaji/lista"))
    return {
      current: "/mfin/periodicniIzvestaji/lista",
      open: ["mfin/periodicniIzvestaji", "/mfin/periodicniIzvestaji"],
    };
  if (url.includes("mfin/trackAndTrace/pracenje"))
    return {
      current: "/mfin/trackAndTrace/pracenje",
      open: ["mfin/trackAndTrace", "/mfin/trackAndTrace"],
    };
  if (url.includes("mfin/trackAndTrace/istorija"))
    return {
      current: "/mfin/trackAndTrace/istorija",
      open: ["mfin/trackAndTrace", "/mfin/trackAndTrace"],
    };
  if (url.includes("mfin/trackAndTrace/monitoring"))
    return {
      current: "/mfin/trackAndTrace/monitoring",
      open: ["mfin/trackAndTrace", "/mfin/trackAndTrace"],
    };
  if (url.includes("mfin/trackAndTrace/izvestajiOPrometu"))
    return {
      current: "/mfin/trackAndTrace/izvestajiOPrometu",
      open: ["mfin/trackAndTrace", "/mfin/trackAndTrace"],
    };
  // MFIN REGISTRI
  if (url.includes("mfin/centralniRegistar/registri"))
    return {
      current: "/mfin/centralniRegistar/registri",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/registarOMarkama"))
    return {
      current: "/mfin/centralniRegistar/registarOMarkama",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/maloprodajneCene"))
    return {
      current: "/mfin/centralniRegistar/maloprodajneCene",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/evidencionaListaMarka"))
    return {
      current: "/mfin/centralniRegistar/evidencionaListaMarka",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/evidencionaListaTrgovci"))
    return {
      current: "/mfin/centralniRegistar/evidencionaListaTrgovci",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/registarSubjekata"))
    return {
      current: "/mfin/centralniRegistar/registarSubjekata",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/registarTrgovaca"))
    return {
      current: "/mfin/centralniRegistar/registarTrgovaca",
      open: ["", "/mfin/centralniRegistar"],
    };
  if (url.includes("mfin/centralniRegistar/objektiPrivrednihSubjekata"))
    return {
      current: "/mfin/centralniRegistar/objektiPrivrednihSubjekata",
      open: ["", "/mfin/centralniRegistar"],
    };

  return { current: "", open: [""] };
};

export const SideLayout = () => {
  const [isLoadingDrustva, setIsLoadingDrustva] = useState<boolean>(false);
  const [selectedDrustvoId, setSelectedDrustvoId] = useState<
    number | undefined
  >(undefined);

  const [current, setCurrent] = useState(getDefaultOpenMenuItem().current);
  const [openKey, setOpenKey] = useState(getDefaultOpenMenuItem().open);
  const [options, setOptions] = useState<any[]>([]);
  const { setAuth } = useContext(AuthContext);
  const { startLoader, endLoader, setError, handleUpdateModals } =
    useContext(ModalsContext);
  const { MenuItemsObligee, MenuItemsMinistry } = useContext(AuthContext);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const userRoles = authService.getRole();

  const handleTabChange: MenuProps["onClick"] = (e) => {
    setCurrent(e.key);
    navigate(e.key);
  };

  const handleLanguageChange = (value: string) => {
    // Delete this if translation works
    // setLanguageHeader(value as Locales);
    //@ts-ignore
    dayjs.updateLocale("en", localeObjectSerbianDayjs(t));
    i18n.changeLanguage(value);
    storage.setItem("locale", value);
    window.location.reload();
  };

  const handleMenuItems = () => {
    if (userRoles.some((role) => role.includes("OBVEZNIK"))) {
      //@ts-ignore
      return MenuItemsObligee(i18n)?.reduce(routeAccessReducer, []) || [];
    } else {
      // @ts-ignore
      return MenuItemsMinistry(i18n)?.reduce(routeAccessReducer, []) || [];
    }
  };

  const fetchUserCompanies = async () => {
    setIsLoadingDrustva(true);

    const [err, res] = await to(authService.getUserCompanies());

    if (err || !res.success) {
      setIsLoadingDrustva(false);
      return;
    }

    setOptions(
      mapSelectOptions({
        sourceArray: res.data,
        valueAccessor: "id",
        labelAccessor: "naziv",
      })
    );

    const selectedDrustvo = res?.data?.find((drustvo) => drustvo?.isSelected);

    if (selectedDrustvo) {
      storage.setItem("userCompanyId", selectedDrustvo?.id as any);

      setSelectedDrustvoId(selectedDrustvo?.id);
    }

    setIsLoadingDrustva(false);
  };

  const handleGotoRegisterCompany: React.MouseEventHandler<HTMLDivElement> = (
    e
  ) => {
    navigate("/activateDrustvo");
  };

  const getUserRoles = async () => {
    const [err, res] = await to(authService.getUserRoles());
    if (err || !res.success) {
      return setError(res?.errorMessage);
    }
    const activeUserRoles = res.data.roles
      .filter((r) => r.aktivno)
      .map((r) => r.name);
    setAuth({
      allUserRoles: activeUserRoles,
      showAddDrustvo: res.data.showAddDrustvo,
    });
    startLoader();

    authService.setShowAddDrustvo(res.data.showAddDrustvo);
    authService.setRole(activeUserRoles);
    await fetchUserCompanies();

    endLoader();

    const getPath = () => {
      let redirectPath = "";
      activeUserRoles.forEach((activeRole: string) => {
        // @ts-ignore
        const roleRedirectPath = USER_REDIRECT_MAP[activeRole];
        if (roleRedirectPath && !redirectPath) {
          redirectPath = roleRedirectPath;
        }
      });
      return redirectPath;
    };

    setTimeout(() => {
      window.location.reload();
    }, 200);
    navigate(getPath(), {});
  };

  const handleSelect = async (id: number) => {
    handleUpdateModals({
      onReload: true,
    });
    const [err, res] = await to(authService.selectUserCompany(id));
    if (err || !res.success) return setError(res?.errorMessage);
    storage.setItem("userCompanyId", id as any);

    getUserRoles();
  };

  const renderSelectDrustvo = () => {
    return authService.getShowAddDrustvo() ? (
      <Select
        placeholder={t("PLACEHOLDERS.DRUSTVA")}
        name="drustva"
        onChange={(value: number) => handleSelect(value)}
        options={options}
        loading={isLoadingDrustva}
        value={selectedDrustvoId}
        className="w-35"
      />
    ) : null;
  };

  const renderAddDrustvoButton = () => {
    return authService.getShowAddDrustvo() ? (
      <Flex
        align="center"
        gap={4}
        className={style.newDrustvoBtn}
        onClick={handleGotoRegisterCompany}
      >
        <PlusOutlined />
        <span className={style.fontSize}>{t("COMMON.DODAJ_SUBJEKT")}</span>
      </Flex>
    ) : null;
  };

  useEffect(() => {
    if (!Number(storage.getItem("userCompanyId"))) {
      navigate("/chooseCompany");
    } else {
      fetchUserCompanies();
    }
  }, []);

  return (
    <ExciseStampsContextProvider>
      <TrackAndTraceContextProvider>
        <AuthorisedPersonsContextProvider>
          <RecordsContextProvider>
            <PeriodicReportsContextProvider>
              <ExciseWarehouseContextProvider>
                <ExciseLicencesContextProvider>
                  <MinistryContextProvider>
                    <CentralRegistryContextProvider>
                      <Layout>
                        <Header className={style.header}>
                          <div className={style.logo}>
                            {t("COMMON.EAKCIZE")}
                          </div>
                          <Flex className={style.headerActions}>
                            {renderAddDrustvoButton()}
                            {renderSelectDrustvo()}
                            <Select
                              suffixIcon={<TranslationOutlined />}
                              onChange={handleLanguageChange}
                              value={storage.getItem("locale") || Locales.LATIN}
                              className={style.languageSelect}
                              options={LANGUAGE_OPTIONS}
                              name="language"
                            />
                            <LogoutBtn />
                          </Flex>
                        </Header>
                        <Layout>
                          <Sider className={style.sidebarWrapper}>
                            <Menu
                              className={style.menu}
                              onClick={handleTabChange}
                              onOpenChange={(openKeys) => {
                                setOpenKey(openKeys);
                              }}
                              selectedKeys={[current]}
                              defaultOpenKeys={openKey}
                              mode="inline"
                              items={
                                handleMenuItems() as ItemType<MenuItemType>[]
                              }
                            />
                          </Sider>
                          <Content className={style.contentWrapper}>
                            <Outlet />
                          </Content>
                        </Layout>
                      </Layout>
                    </CentralRegistryContextProvider>
                  </MinistryContextProvider>
                </ExciseLicencesContextProvider>
              </ExciseWarehouseContextProvider>
            </PeriodicReportsContextProvider>
          </RecordsContextProvider>
        </AuthorisedPersonsContextProvider>
      </TrackAndTraceContextProvider>
    </ExciseStampsContextProvider>
  );
};
